import { Controller } from "@hotwired/stimulus"

// Billing page (Phelipe 2026-10-30): "Switch to yearly · save 20%" asks first,
// then switches in place and updates the price, billing and next-bill cells.
// Wire plans (2026-10-31): "Don't renew" asks first, "Renew after all" undoes
// it; both swap the status pill, next-bill cell and wire footer in place.
// Change plan (2026-10-31): pick Starter / Pro / Business, then switch in
// place: plan name, price, next bill, the pop-up's list and the sidebar.
export default class extends Controller {
  static targets = ["modal", "error", "confirmButton", "switchButton", "price", "interval", "nextBill", "note",
                    "renewModal", "renewError", "renewConfirm", "renewReason", "renewMissing", "wireFooter", "statusPill",
                    "cancelModal", "planModal", "planOptions", "planOption", "planNote", "planError", "planConfirm",
                    "planName", "headline", "switchBody"]
  static values = { switchUrl: String, renewUrl: String, planUrl: String, currentCents: Number }

  openSwitch() {
    this.errorTarget.classList.add("hidden")
    this.confirmButtonTarget.disabled = false
    this.modalTarget.classList.remove("hidden")
  }

  closeSwitch() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async confirmSwitch() {
    this.confirmButtonTarget.disabled = true
    try {
      const response = await fetch(this.switchUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "")

      this.priceTarget.textContent = data.price
      this.intervalTarget.textContent = data.interval
      this.nextBillTarget.textContent = data.next_bill
      if (this.hasSwitchButtonTarget) this.switchButtonTarget.remove()
      if (this.hasNoteTarget) this.noteTarget.classList.add("hidden")
      this.closeSwitch()
      this._showToast(data.message)
    } catch (error) {
      this.errorTarget.textContent = error.message
      this.errorTarget.classList.toggle("hidden", !error.message)
      this.confirmButtonTarget.disabled = false
    }
  }

  openPlan() {
    this.planOptionTargets.forEach((input) => { input.checked = false })
    this.planNoteTarget.classList.add("hidden")
    this.planErrorTarget.classList.add("hidden")
    this.planConfirmTarget.disabled = true
    this.planModalTarget.classList.remove("hidden")
  }

  closePlan() {
    this.planModalTarget.classList.add("hidden")
  }

  // Says whether they pay the difference today or get credit, per month.
  pickPlan(event) {
    const diff = Number(event.target.dataset.cents) - this.currentCentsValue
    const note = this.planNoteTarget
    note.textContent = diff > 0 ? note.dataset.up : note.dataset.down
    note.classList.toggle("hidden", diff === 0)
    this.planConfirmTarget.disabled = false
  }

  async confirmPlan() {
    const picked = this.planOptionTargets.find((input) => input.checked)
    if (!picked) return

    this.planConfirmTarget.disabled = true
    try {
      const response = await fetch(this.planUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ plan: picked.value })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "")

      this.planNameTarget.textContent = data.plan_name
      this.priceTarget.textContent = data.price
      this.nextBillTarget.textContent = data.next_bill
      if (this.hasHeadlineTarget) this.headlineTarget.remove()
      if (this.hasNoteTarget) this.noteTarget.textContent = data.note
      if (this.hasSwitchBodyTarget) this.switchBodyTarget.textContent = data.switch_body
      this.planOptionsTarget.innerHTML = data.options_html
      this.currentCentsValue = data.current_cents
      document.querySelectorAll(".js-sidebar-plan-name").forEach((el) => { el.textContent = data.plan_name })
      this.closePlan()
      this._showToast(data.message)
    } catch (error) {
      this.planErrorTarget.textContent = error.message
      this.planErrorTarget.classList.toggle("hidden", !error.message)
      this.planConfirmTarget.disabled = false
    }
  }

  // Cancel plan (card/bank plans): "Before you go" first; its form goes on to Stripe's cancel page.
  openCancel() {
    this.cancelModalTarget.classList.remove("hidden")
  }

  closeCancel() {
    this.cancelModalTarget.classList.add("hidden")
  }

  openStopRenewal() {
    this.renewErrorTarget.classList.add("hidden")
    this.renewConfirmTarget.disabled = false
    this.renewModalTarget.classList.remove("hidden")
  }

  closeStopRenewal() {
    this.renewModalTarget.classList.add("hidden")
  }

  async confirmStopRenewal() {
    this.renewConfirmTarget.disabled = true
    try {
      await this._setRenewal(false)
      this.closeStopRenewal()
    } catch (error) {
      this.renewErrorTarget.textContent = error.message
      this.renewErrorTarget.classList.toggle("hidden", !error.message)
      this.renewConfirmTarget.disabled = false
    }
  }

  async resumeRenewal(event) {
    const button = event.currentTarget
    button.disabled = true
    try {
      await this._setRenewal(true)
    } catch (error) {
      button.disabled = false
      if (error.message) this._showToast(error.message, "alert")
    }
  }

  async _setRenewal(renew) {
    const response = await fetch(this.renewUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify(renew ? { renew: "1" } : {
        renew: "0", reason: this.renewReasonTarget.value, missing_feature: this.renewMissingTarget.value
      })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || "")

    this.wireFooterTarget.outerHTML = data.footer_html
    this.statusPillTarget.outerHTML = data.status_html
    this.nextBillTarget.textContent = data.next_bill
    this._showToast(data.message)
  }

  _showToast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = `flash-toast-${type}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
